import { apiClient as api, authHeaders } from './api';

const BASE = '/api/v3/voice';

export interface VoiceTranscriptChunk {
  type: 'transcript' | 'partial' | 'final' | 'error';
  text?: string;
  message?: string;
}

export interface TranscribeOptions {
  language?: string;
  signal?: AbortSignal;
  onChunk?: (chunk: VoiceTranscriptChunk) => void;
}

/**
 * Voice transcription. Posts an audio Blob (recommended: opus/webm 250ms chunks
 * per spec §4.5) and consumes a Server-Sent Events transcript stream.
 *
 * BE surface lands in B24; this implementation matches the planned shape so
 * F17 (voice capture) wires straight through. The endpoint opts out of the
 * envelope filter — the response is event-stream, not ApiResponse<T>.
 */
export const VoiceService = {
  async transcribe(audio: Blob, options: TranscribeOptions = {}): Promise<string> {
    const formData = new FormData();
    formData.append('audio', audio);
    if (options.language) formData.append('language', options.language);

    const baseURL = api.defaults.baseURL ?? '';
    const response = await fetch(`${baseURL}${BASE}/transcribe`, {
      method: 'POST',
      body: formData,
      signal: options.signal,
      headers: authHeaders(),
    });
    if (!response.ok || !response.body) {
      throw new Error(`Voice transcribe failed: ${response.status} ${response.statusText}`);
    }
    return consumeSseTranscript(response.body, options.onChunk);
  },
};

async function consumeSseTranscript(
  body: ReadableStream<Uint8Array>,
  onChunk?: (chunk: VoiceTranscriptChunk) => void,
): Promise<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalText = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const events = buffer.split('\n\n');
    buffer = events.pop() ?? '';

    for (const raw of events) {
      const dataLine = raw
        .split('\n')
        .find(line => line.startsWith('data:'));
      if (!dataLine) continue;
      const payload = dataLine.slice(5).trim();
      if (!payload) continue;
      try {
        const chunk = JSON.parse(payload) as VoiceTranscriptChunk;
        onChunk?.(chunk);
        if (chunk.type === 'final' && chunk.text) finalText = chunk.text;
      } catch {
        // Non-JSON keepalive — ignore.
      }
    }
  }
  return finalText;
}
